// import { Attachment, FileGenerationStatus, Message, ReplyMessageData } from '../types/chat';
import { Attachment, Message, MessageRole, Source } from '../types/chat';
import { CustomGpt } from '../types/custom-gpt.types';
import { CHAT_MAIN_COLUMN_CLASS } from 'constant/chat-layout';
import { CustomGptHeader } from './CustomGptHeader';

import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, memo } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink } from 'lucide-react';

import { ArrowUp, Copy, EditPencil, Xmark } from 'iconoir-react';
import {
  CHAT_MESSAGE_ACTION_ICON_PROPS,
} from 'constant/chat-layout';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  getConnectorBadgeInfo,
  CONNECTOR_BADGE_CLASS,
  CONNECT_ACTION_TEXT,
} from '../utils/connector-link-badges';
import { ConnectorLinkCard } from './connector-link-card';
import { cn } from 'lib/utils';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { UserMessageAttachments } from './attachment-file-card';
import { useToast } from '../../../hooks/use-toast';
import ExpandableThinkContainer from './ExpandableThinkContainer';
import { useTranslation } from 'react-i18next';
import { Feedback } from './Feedback';
import { useTheme } from '../../../components/core/theme-provider';
import { ChangeMessage } from './ChangeMessage';
import { AssistantStreamingRow, shouldRenderAssistantStreamingRow } from './AssistantStreamingRow';
import { AssistantAvatar } from './MessageLoading';
import { ScrollToBottomControl } from './ScrollToBottomControl';
import { isAwayFromBottom } from '../utils/scroll-request-visibility';
import { useScrollToLatestOnOpen } from '../hooks/use-scroll-to-latest-on-open';
import { CancelledResponseNotice } from './CancelledResponseNotice';
import { CreditsEmptyNotice } from 'features/credits/components/credits-empty-notice';
import { AssistantAttachmentPreview } from './AssistantAttachmentPreview';
import { TryAgentBadgesList } from './TryAgentBadge';
import { MessageA2uiSurfaces } from './a2ui/MessageA2uiSurfaces';
import {
  conversationTurnHasPendingApproval,
  isLatestApprovalTurn,
} from '../utils/approval-conversation-messages';
import { generationHasPendingApproval, hasStepsStreamBlock } from '../utils/stream-blocks';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useAssistantFileViewerStore } from '../../../state/store/chat/use-assistant-file-viewer-store';
import {
  canOpenAssistantAttachmentInViewer,
  getLastDashboardHtmlAttachment,
  getLastViewableAssistantAttachment,
  hasDashboardFile,
  isDashboardAttachment,
  shouldPreviewHtmlInDashboard,
} from '../utils/assistant-file-preview';
import SourceFile from './SourceFile';
import { getDisplayableSources } from '../utils/source-display';
import { CitationMarker } from './citation/CitationMarker';
import { rehypeCitations } from './citation/rehype-citations';
import {
  getCitedPage,
  highlightSourcePill,
  pillDomId,
  resolvePillByCitationIndex,
} from '../utils/source-citations';
import { useOpenSourceFile } from '../utils/use-open-source-file';
import { useSyncDashboardVersions } from '../hooks/use-dashboard-title';
import { formatBubbleHoverTime, parseMessageTimeForDisplay } from '../utils/message-show-time';
import {
  hasInlineStreamSteps,
  mergeStreamStatusEntriesFromGenerations,
  resolveStreamActivityEntries,
} from '../utils/stream-status-entries';
import { isValidUrl, placeHrefInContent } from 'utils/chat-utilities';
import { SearchSources } from './SearchSources';
import SearchRelatedList from './SearchRelatedList';
import SearchRelatedImageList from './SearchRelatedImageList';
import SearchRelatedVideoList from './SearchRelatedVideoList';
import { AnalyzingIndicator } from './AnalyzingIndicator';
import { StreamActivityDropdown } from './StreamActivityDropdown';
import { InterleavedStreamContent } from './InterleavedStreamContent';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import {
  consolidateAssistantMessages,
  removeEchoUserMessageRows,
  resolveDisplayGenerationIndex,
  resolveUserMessageRecordIdForAssistant,
  escapeMarkdownNumbers,
  normalizeBareJsonCodeBlocks,
} from '../../../utils/chat-utilities';

import { useConfig } from '../../../hooks/use-config';
import { useModelStore } from 'state/store/chat/use-model-store';
import {
  ImageGenerationSkeleton,
  ASSISTANT_IMAGE_MARGIN_CLASS,
  isImageGenerationPendingContent,
} from './ImageGenerationSkeleton';
import { MarkdownChatImage } from './markdown-chat-image';
import { CopyMessageDropdown } from './CopyMessageDropdown';
import { ChatCodeBlock } from './ChatCodeBlock';
import { isOnboardingConversationId } from 'features/onboarding/onboarding-script';

const markdownComponents = {
  h1: ({ children }: any) => <h1 className="text-3xl font-bold mb-2 last:mb-0">{children}</h1>,
  h2: ({ children }: any) => (
    <h2 className="text-xl font-semibold mb-2 last:mb-0">{children}</h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="text-lg font-semibold mb-2 last:mb-0">{children}</h3>
  ),
  h4: ({ children }: any) => <h4 className="text-base font-medium mb-2 last:mb-0">{children}</h4>,
  h5: ({ children }: any) => (
    <h5 className="text-xs font-medium mb-2 last:mb-0">{children}</h5>
  ),
  h6: ({ children }: any) => (
    <h6 className="text-[10.72px] font-medium mb-2 last:mb-0">{children}</h6>
  ),
  p: ({ children }: any) => (
    <p className="mb-2 last:mb-0 break-words text-sm">{children}</p>
  ),
  ul: ({ children }: any) => <ul className="list-disc pl-4 mb-2 text-sm">{children}</ul>,
  ol: ({ children, start }: any) => <ol start={start} className="list-decimal pl-4 mb-2">{children}</ol>,
  li: ({ children }: any) => <li className="mb-0.5 text-sm">{children}</li>,
  code: ChatCodeBlock,
  pre: ({ children }: any) => <div className="w-full mb-2">{children}</div>,
  blockquote: ({ children }: any) => (
    <blockquote className="border-l-4 border-gray-600 pl-4 italic mb-2">{children}</blockquote>
  ),
  table: ({ children }: any) => (
    <div className="mb-2 overflow-x-auto">
      <Table>{children}</Table>
    </div>
  ),
  thead: ({ children }: any) => <TableHeader>{children}</TableHeader>,
  tbody: ({ children }: any) => <TableBody>{children}</TableBody>,
  tr: ({ children }: any) => <TableRow>{children}</TableRow>,
  th: ({ children }: any) => <TableHead>{children}</TableHead>,
  td: ({ children }: any) => <TableCell>{children}</TableCell>,
  a: ({ href, children }: any) => {
    if (children == null || children === '') return null;

    const extractText = (node: any): string => {
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return String(node);
      if (Array.isArray(node)) return node.map(extractText).join('');
      if (node && typeof node === 'object' && node.props?.children) {
        return extractText(node.props.children);
      }
      return '';
    };

    const text = extractText(children);
    const connector = getConnectorBadgeInfo(text, href);

    if (connector && connector.name !== 'Agent' && CONNECT_ACTION_TEXT.test(text)) {
      return (
        <ConnectorLinkCard href={href} name={connector.name} icon={connector.icon} description={text} />
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'group mx-1 my-0.5 inline-flex items-center gap-1.5 align-middle rounded-full px-2.5 py-0.5 text-xs font-medium cursor-pointer shadow-xs transition-all duration-150 select-none no-underline active:scale-95 border border-gray-300 dark:border-gray-500',
          connector
            ? connector.className
            : CONNECTOR_BADGE_CLASS
        )}
      >
        {connector?.icon}
        <span>{children}</span>
        <ExternalLink className="h-3 w-3 shrink-0 opacity-70 transition-opacity group-hover:opacity-100" />
      </a>
    );
  },
  img: MarkdownChatImage,
};

interface CitationContext {
  sources: Source[];
  onCitationClick: (index: number) => void;
}

interface StreamingContentProps {
  content: string;
  isUserMessage: boolean;
  isAnalyzing?: boolean;
  /** When present, inline `[[ref:n]]` tokens render as clickable citation markers. */
  citation?: CitationContext;
}

const closeUnterminatedMarkdownLink = (content: string) => {
  const withClosedLink = content.replace(
    /\[([^\]]*)\]\((https?:\/\/[^\s)]+)$/i,
    '[$1]($2)'
  );
  return withClosedLink.replace(
    /(\*\*\[[^\]]*\]\(https?:\/\/[^)]+\))$/i,
    '$1**'
  );
};

const StreamingContent = memo(({ content, isUserMessage, isAnalyzing, citation }: StreamingContentProps) => {
  const text = typeof content === 'string' ? content : '';
  const processedContent = isUserMessage
    ? escapeMarkdownNumbers(text)
    : isAnalyzing
      ? text
      : closeUnterminatedMarkdownLink(normalizeBareJsonCodeBlocks(text));

  const components = citation
    ? ({
        ...markdownComponents,
        citationref: (props: { node?: { properties?: { dataRefIndex?: string } } }) => (
          <CitationMarker
            {...props}
            sources={citation.sources}
            onCitationClick={citation.onCitationClick}
          />
        ),
      } as unknown as Components)
    : (markdownComponents as unknown as Components);

  const rehypePlugins = citation ? [rehypeRaw, rehypeCitations] : [rehypeRaw];

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={rehypePlugins}
      components={components}
    >
      {processedContent}
    </ReactMarkdown>
  );
});

StreamingContent.displayName = 'StreamingContent';

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  streamingMessage: Message | undefined;
  streamingAssistantMessageId?: string | null;
  isStillGenerating: boolean;
  chatInputHeight?: number;
  onSendMessage?: (content: string, files: Attachment[]) => void;
  currentUser?: { profileImageUrl?: string; itemId?: string } | null;
  associatedAssistant?: CustomGpt | null;
  scrollButtonPortalHost?: HTMLDivElement | null;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  agentModelLabel?: string | null;
  agentId?: string | null;
  agentLogoUrl?: string | null;
}

const getStreamingGenerationIndex = (
  message: Message,
  streamingAssistantMessageId?: string | null
): number => {
  if (streamingAssistantMessageId) {
    const byId = message.generations.findIndex((g) => g.id === streamingAssistantMessageId);
    if (byId !== -1) return byId;
  }
  const ongoingIdx = message.generations.findIndex((g) => g.isThisGenerationOnGoing);
  if (ongoingIdx !== -1) return ongoingIdx;
  return Math.max(0, message.generations.length - 1);
};

export const ChatMessages = ({
  messages,
  isLoading,
  streamingMessage,
  streamingAssistantMessageId,
  isStillGenerating,
  chatInputHeight = 186,
  onSendMessage,
  associatedAssistant,
  scrollButtonPortalHost,
  scrollContainerRef,
  agentModelLabel,
  agentId,
  agentLogoUrl,
}: ChatMessagesProps) => {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');

  // console.log('isStillGenerating', isStillGenerating)


  // const setCommentMessage = useChatStore((state) => state.setCommentMessage);
  // const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  // const setReplyMessage = useChatStore((state) => state.setReplyMessage);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showScrollControl, setShowScrollControl] = useState(false);
  // const { pathname } = useLocation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const { openSourceFile } = useOpenSourceFile();

  const displayMessages = useMemo(
    () => consolidateAssistantMessages(removeEchoUserMessageRows(messages)),
    [messages]
  );
  // Numbers dashboard versions saved before the backend stored one.
  useSyncDashboardVersions(messages);

  const latestAssistantRecordId = useMemo(
    () => [...displayMessages].reverse().find((message) => message.role === 'assistant')?.recordId,
    [displayMessages]
  );
  const latestRequestId = useMemo(
    () => [...displayMessages].reverse().find((message) => message.role === 'user')?.recordId,
    [displayMessages]
  );

  const isNewChat = useChatStore((state) => state.isNewChat);
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const isOnboardingChat = isOnboardingConversationId(activeConversationId);
  const itemIdFilePathMap = useChatStore((state) => state.itemIdFilePathMap);
  const openAssistantFile = useAssistantFileViewerStore((state) => state.openFile);
  const openDashboardHtml = useAssistantFileViewerStore((state) => state.openDashboardHtml);
  const closeDashboardHtml = useAssistantFileViewerStore((state) => state.closeDashboardHtml);
  const lastAutoOpenedFileIdRef = useRef<string | null>(null);
  const dashboardHtmlIdRef = useRef<string | null>(null);
  const liveTurnRef = useRef(false);
  const isDashboardChat = useAssistantFileViewerStore((state) => state.isDashboardConversation);
  const markLiveDashboard = useAssistantFileViewerStore((state) => state.markLiveDashboard);
  const setDashboardFileInThread = useAssistantFileViewerStore(
    (state) => state.setDashboardFileInThread
  );
  const threadHasDashboardFile = useMemo(() => hasDashboardFile(displayMessages), [displayMessages]);

  useEffect(() => {
    setDashboardFileInThread(threadHasDashboardFile);
  }, [threadHasDashboardFile, setDashboardFileInThread]);

  useEffect(() => {
    lastAutoOpenedFileIdRef.current = null;
    dashboardHtmlIdRef.current = null;
    liveTurnRef.current = false;
  }, [activeConversationId]);

  useEffect(() => {
    if (isStillGenerating) {
      liveTurnRef.current = true;
    }
  }, [isStillGenerating]);

  const lastDisplayMessage = displayMessages[displayMessages.length - 1];
  useEffect(() => {
    if (lastDisplayMessage?.role === 'user') {
      liveTurnRef.current = false;
      lastAutoOpenedFileIdRef.current = null;
    }
  }, [lastDisplayMessage?.recordId, lastDisplayMessage?.role]);

  useEffect(() => {
    if (!liveTurnRef.current) return;

    const latestAssistant = [...displayMessages]
      .reverse()
      .find((message) => message.role === 'assistant');
    if (!latestAssistant) return;

    const generationIndex =
      streamingAssistantMessageId &&
      latestAssistant.generations.some((generation) => generation.id === streamingAssistantMessageId)
        ? getStreamingGenerationIndex(latestAssistant, streamingAssistantMessageId)
        : resolveDisplayGenerationIndex(latestAssistant);

    const liveAttachments = latestAssistant.generations[generationIndex]?.attachments;
    // A dashboard saved on the live turn of any chat makes it a dashboard chat.
    const liveDashboard = (liveAttachments ?? []).some(
      (file) =>
        file.sender === MessageRole.ASSISTANT &&
        canOpenAssistantAttachmentInViewer(file) &&
        isDashboardAttachment(file.fileName, file.extension)
    );
    if (liveDashboard && !isDashboardChat && activeConversationId) {
      markLiveDashboard(activeConversationId);
    }

    const lastAttachment = getLastViewableAssistantAttachment(liveAttachments);
    if (!lastAttachment) return;
    if (lastAutoOpenedFileIdRef.current === lastAttachment.fileId) return;

    lastAutoOpenedFileIdRef.current = lastAttachment.fileId;
    // The dashboard canvas shows a dashboard; it never opens in the file preview.
    if (isDashboardAttachment(lastAttachment.fileName, lastAttachment.extension)) return;
    if (
      shouldPreviewHtmlInDashboard(
        lastAttachment.fileName,
        lastAttachment.extension,
        isDashboardChat,
        threadHasDashboardFile
      )
    ) {
      return;
    }
    openAssistantFile(lastAttachment);
  }, [
    displayMessages,
    streamingAssistantMessageId,
    isStillGenerating,
    openAssistantFile,
    isDashboardChat,
    threadHasDashboardFile,
    activeConversationId,
    markLiveDashboard,
  ]);

  useEffect(() => {
    if (!isDashboardChat) {
      closeDashboardHtml();
      return;
    }

    const lastHtml = getLastDashboardHtmlAttachment(displayMessages);
    if (!lastHtml) {
      closeDashboardHtml();
      dashboardHtmlIdRef.current = null;
      return;
    }
    if (dashboardHtmlIdRef.current === lastHtml.fileId) return;

    dashboardHtmlIdRef.current = lastHtml.fileId;
    openDashboardHtml(lastHtml);
  }, [isDashboardChat, displayMessages, closeDashboardHtml, openDashboardHtml]);

  const streamingUserMessageRecordId =
    isStillGenerating && streamingMessage
      ? resolveUserMessageRecordIdForAssistant(displayMessages, streamingMessage) ??
        streamingMessage.userRecordId
      : undefined;

  const scrollToBottom = (smooth = false) => {
    const container = scrollContainerRef?.current;
    if (!container) return;
    // Scroll the message pane only. scrollIntoView walks ancestors and pulls the composer up.
    if (smooth && typeof container.scrollTo === 'function') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      return;
    }
    container.scrollTop = container.scrollHeight;
  };

  const syncScrollControl = useCallback(() => {
    setShowScrollControl(isAwayFromBottom(scrollContainerRef?.current ?? null));
  }, [scrollContainerRef]);

  useLayoutEffect(() => {
    syncScrollControl();
  }, [syncScrollControl, displayMessages, isStillGenerating]);

  useScrollToLatestOnOpen(scrollContainerRef, activeConversationId, displayMessages.length > 0);

  const lastMessageRecordId = messages[messages.length - 1]?.recordId;
  const wasStreamingRef = useRef(false);
  // Follow the reply while it streams. Left off so the view stays put.
  // const isStreamingCurrentMessage =
  //   isStillGenerating && !!lastMessageRecordId && lastMessageRecordId === streamingMessage?.recordId;
  //
  // useEffect(() => {
  //   if (isNewChat) return;
  //   scrollToBottom();
  // }, [isStreamingCurrentMessage, isNewChat]);

  useEffect(() => {
    const streamingStarted = isStillGenerating && !wasStreamingRef.current;
    const streamingEnded = !isStillGenerating && wasStreamingRef.current;
    wasStreamingRef.current = isStillGenerating;
    if (isNewChat || messages.length === 0) return;
    // Jump once when the user sends. Do not keep following the reply.
    if (streamingStarted) {
      scrollToBottom();
      return;
    }
    if (streamingEnded || isStillGenerating) return;
    if (scrollContainerRef?.current) {
      const { scrollTop, clientHeight, scrollHeight } = scrollContainerRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearBottom) {
        scrollToBottom();
      }
    } else {
      scrollToBottom();
    }
  }, [messages.length, lastMessageRecordId, scrollContainerRef, isNewChat, isStillGenerating]);

  // Stop in-flight smooth scroll when this list unmounts (e.g. New Chat).
  useEffect(() => {
    const container = scrollContainerRef?.current;
    return () => {
      // Re-assigning scrollTop cancels any in-flight smooth scroll.
      if (container) {
        const { scrollTop } = container;
        container.scrollTop = scrollTop;
      }
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return undefined;

    container.addEventListener('scroll', syncScrollControl);
    const observer = new ResizeObserver(() => syncScrollControl());
    observer.observe(container);
    if (container.firstElementChild instanceof HTMLElement) {
      observer.observe(container.firstElementChild);
    }

    return () => {
      container.removeEventListener('scroll', syncScrollControl);
      observer.disconnect();
    };
  }, [scrollContainerRef, syncScrollControl]);

  const scrollToBottomButtonUi = showScrollControl ? (
    <ScrollToBottomControl
      isStreaming={isStillGenerating}
      bottomOffset={chatInputHeight - 40}
      onClick={() => scrollToBottom(true)}
    />
  ) : null;

  const fallbackCopyToClipboard = (text: string) => {
    const tempTextArea = document.createElement('textarea');
    tempTextArea.value = text;
    document.body.appendChild(tempTextArea);
    tempTextArea.select();

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Failed to copy using execCommand: ', err);
    } finally {
      document.body.removeChild(tempTextArea);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text);
      } else {
        fallbackCopyToClipboard(text);
      }
      toast({
        variant: 'success',
        title: t('COPIED'),
        description: t('TEXT_COPY_SUCCESS'),
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_TEXT'),
        description: t('FAILED_COPY_TO_CLIPBOARD'),
      });
    }
  };

  const copyImageToClipboard = async (fileId: string) => {
    try {
      const itemIdFilePathMap = useChatStore.getState().itemIdFilePathMap;
      const imageSrc = itemIdFilePathMap.get(fileId);

      if (!imageSrc) {
        toast({
          variant: 'destructive',
          title: t('FAILED_TO_COPY_IMAGE'),
          description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD'),
        });
        return;
      }

      try {
        const response = await fetch(imageSrc);
        if (!response.ok) {
          trackEvent(
            EVENTS.ERROR(new Error('Failed to copy image'), {
              endpoint: imageSrc,
              method: 'GET',
              status: response.status,
            })
          );
        }
        const blob = await response.blob();
        const imageBlob = new Blob([blob], { type: 'image/png' });

        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': imageBlob,
          }),
        ]);

        toast({
          variant: 'success',
          title: t('COPIED'),
          description: t('IMAGE_COPY_SUCCESS'),
        });
        return;
      } catch (clipboardError) {
        console.warn('Clipboard API failed, falling back to download:', clipboardError);
      }
    } catch (error) {
      console.error('Failed to copy/download image:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_IMAGE'),
        description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD'),
      });
    }
  };

  const handleEditMessage = (messageId: string, content: string, userRecordId: string) => {
    if (isStillGenerating && userRecordId !== streamingUserMessageRecordId) return;
    setEditingMessageId(messageId);
    setEditedContent(content);
  };

  const handleSaveEdit = async (messageId: string) => {
    const trimmedContent = editedContent.trim();
    if (!trimmedContent) return;
    if (isStillGenerating && messageId !== streamingUserMessageRecordId) return;

    const { activeConversationId, conversations } = useChatStore.getState();
    if (!activeConversationId) return;

    const currentConv = conversations[activeConversationId];
    if (!currentConv) return;

    const userMessage = currentConv.messages.find((msg) => msg.recordId === messageId);
    if (!userMessage) return;

    const firstGeneration = userMessage.generations[0];
    const originalAttachments =
      firstGeneration?.attachments?.filter((att) => att.sender === 'user') || [];

    setEditingMessageId(null);
    setEditedContent('');

    const selectedModel = useModelStore.getState().selectedModel;

    await useChatStore.getState().editUserMessage(
      messageId,
      trimmedContent,
      configurationSettings,
      {
        attachments: originalAttachments,
        selectedModelId: selectedModel?.id,
      }
    );
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent('');
  };

  const copyChatBubbleIcons = [
    {
      icon: <Copy {...CHAT_MESSAGE_ACTION_ICON_PROPS} />,
      onClick: async (text: string, hasImage?: boolean, fileId?: string) => {
        if (hasImage && fileId && navigator.clipboard && window.isSecureContext) {
          await copyImageToClipboard(fileId);
        } else handleCopyToClipboard(text);
      },
      type: 'icon',
      tooltip: t('COPY'),
    },
  ];

  const messageBubbleUi = (
    message: Message,
    index: number,
    isStillGenerating: boolean,
    isLatestTurn = true,
  ) => {
    const generation = message.generations[index];
    // const checkStreamingMessage = () => {
    //   if (message.role === 'assistant') {
    //     console.log('generation in ChatMessages', generation?.isThisGenerationOnGoing)

    //   }

    // }
    // checkStreamingMessage();

    // can we use this code {"step_type": "action", "tool": "python_interpreter", "code": "import pandas as pd\nimport matplotlib.pyplot as plt\n\n# Read the Excel file into a DataFrame\ntry:\n df = pd.read_excel('/tmp/code_interpreter_rusgt571/random_people_200plus.xlsx')\nexcept Exception as e:\n print(f"Failed to read Excel file: {e}")\n # Handle the exception appropriately\n\n# Check if the DataFrame is not empty\nif not df.empty:\n # Assuming there's a column named 'values' or a similar numeric column\n numeric_columns = df.select_dtypes(include=['int64', 'float64']).columns\n \n if len(numeric_columns) > 0:\n # Use the first numeric column found for analysis\n value_column = numeric_columns[0]\n \n # Get the top 10 values\n top_10 = df.nlargest(10, value_column)\n \n # Create a bar chart\n plt.figure(figsize=(10,6))\n plt.bar(top_10.index, top_10[value_column])\n plt.xlabel('Index')\n plt.ylabel(value_column)\n plt.title(f'Top 10 {value_column}')\n plt.xticks(rotation=90) # Rotate x-axis labels for better readability\n \n # Save the plot\n plt.savefig('/tmp/code_artifacts_cchg7pnw/top_10_values_plot.png')\n plt.close()\n \n print("Top 10 values and plot created successfully.")\n else:\n print("No numeric columns found in the data.")\nelse:\n print("The DataFrame is empty.")", "observations": "Execution logs:\nTop 10 values and plot created successfully.\nLast output from code snippet:\n(array([-20., 0., 20., 40., 60., 80., 100.]), [Text(-20.0, 0, '\u221220'), Text(0.0, 0, '0'), Text(20.0, 0, '20'), Text(40.0, 0, '40'), Text(60.0, 0, '60'), Text(80.0, 0, '80'), Text(100.0, 0, '100')])"}}
    // in react markdown

    if (generation.searchSources && typeof generation.content === 'string') {
      generation.content = placeHrefInContent(generation.content, generation.searchSources);
      generation.searchSources = generation.searchSources.filter((source) => {
        return source.metadata?.url && isValidUrl(source.metadata.url);
      });
    }

    // Add code execution steps to content if they exist

    const assistantAttachments = generation.attachments?.filter(
      (attachment) => attachment.sender === 'assistant'
    );
    // console.log('assistantAttachments in ChatMessages', assistantAttachments)
    const assistantGeneratedImages = generation.generatedImages?.filter(
      (attachment) => attachment.sender === 'assistant'
    );

    const userAttachments = generation.attachments?.filter(
      (attachment) => attachment.sender === 'user'
    );
    const sources = getDisplayableSources(generation?.sources || []);

    // Full (unfiltered) sources resolve inline [[ref:n]] markers to a pill.
    const citationSources = generation?.sources ?? [];
    const citation =
      message.role === 'assistant' && citationSources.length > 0
        ? {
            sources: citationSources,
            onCitationClick: (n: number) => {
              const source = resolvePillByCitationIndex(citationSources, n);
              if (!source) return;
              openSourceFile(source, getCitedPage(source));
              highlightSourcePill(pillDomId(generation.id, source));
            },
          }
        : undefined;

    const handleSendMessage = (message: string, files: any[]) => {
      onSendMessage?.(message, files);
    };
    // console.log('generation', generation)

    const isEditing = editingMessageId === `${message.recordId}-${generation.id}`;
    const messageTime = parseMessageTimeForDisplay(generation.showMessageTime, generation.createdDate);
    const messageTimeLabel = messageTime ? formatBubbleHoverTime(messageTime) : null;
    const codeExecutionSteps = (generation as { codeExecutionSteps?: unknown[] }).codeExecutionSteps;
    const hasCodeExecutionSteps = Array.isArray(codeExecutionSteps) && codeExecutionSteps.length > 0;
    const showAnalyzing =
      !!generation.isAnalyzing && !(generation.thinkContent && !generation.content);
    const showAnalyzed =
      !!generation.isAnalyzed || (hasCodeExecutionSteps && !generation.isAnalyzing);
    const streamActivityEntries = resolveStreamActivityEntries(generation);
    const usesInlineStreamSteps = hasInlineStreamSteps(generation.streamStatusEntries);
    // Timeline already owns Steps — do not also render the history dropdown above the answer.
    const showStreamActivity =
      message.role === 'assistant' &&
      typeof generation?.content === 'string' &&
      !!generation.content.trim() &&
      streamActivityEntries.length > 0 &&
      !usesInlineStreamSteps &&
      !hasStepsStreamBlock(generation.streamBlocks);

    const isStreamingTurnAssistant =
      message.role === 'assistant' &&
      streamingMessage != null &&
      message.recordId === streamingMessage.recordId;

    const feedbackIsStillGenerating = generation.isCancelled
      ? false
      : isStreamingTurnAssistant
        ? isStillGenerating || !!(generation?.isThisGenerationOnGoing ?? false)
        : !!(generation?.isThisGenerationOnGoing ?? false);

    const isImageGenerationPending =
      message.role === 'assistant' && isImageGenerationPendingContent(generation.content);

    return (
      <div
        className={`${message.role === 'user' ? (isEditing ? 'w-full' : 'min-w-full') : 'w-full flex flex-col'} mb-1`}
      >
        {isImageGenerationPending ? (
          <ImageGenerationSkeleton className={ASSISTANT_IMAGE_MARGIN_CLASS} />
        ) : (
          <>
            <div
              className={`flex min-w-0 ${
                message.role === 'user' && !isEditing
                  ? 'w-full flex-col items-end gap-2'
                  : 'items-center'
              } ${message.role === 'user' && isEditing ? 'justify-start' : message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'user' &&
                !isEditing &&
                userAttachments &&
                userAttachments.length > 0 && (
                  <UserMessageAttachments
                    files={userAttachments}
                    isDark={theme === 'dark'}
                    previewUrlById={itemIdFilePathMap}
                  />
                )}
              {(message.role !== 'user' ||
                isEditing ||
                Boolean(generation.content?.trim())) && (
              <div
                className={`${isEditing ? 'w-full relative rounded-2xl border shadow-md p-4' : `${message.role == 'user' ? (theme === 'dark' ? 'bg-black-200' : 'bg-[#F6F6F6]') : ''} ${message.role === 'user' ? 'w-fit max-w-full' : 'w-full'} px-4 py-2 rounded-[12px]`} ${isEditing ? (theme === 'dark' ? 'bg-[#272727] border-gray-600' : 'bg-white border-gray-200') : ''}`}
              >
                <div className="break-words min-w-0 text-sm">
                  {message.role === 'user' &&
                    isEditing &&
                    userAttachments &&
                    userAttachments.length > 0 && (
                      <UserMessageAttachments
                        files={userAttachments}
                        isDark={theme === 'dark'}
                        previewUrlById={itemIdFilePathMap}
                      />
                    )}
                  {generation.searchSources && generation.searchSources.length > 0 && (
                    <SearchSources sources={generation.searchSources} />
                  )}
                  {generation.thinkContent && (
                    <ExpandableThinkContainer
                      content={generation.thinkContent ?? ''}
                      isThinking={generation.isThinking}
                      className="mb-2"
                    />
                  )}
                  {showAnalyzing && (
                    <AnalyzingIndicator isAnalyzingStep={false} isAnalyzing={true} />
                  )}
                  {showAnalyzed && (
                    <AnalyzingIndicator
                      isAnalyzingStep={true}
                      isAnalyzing={false}
                      codeExecutionSteps={codeExecutionSteps as any}
                    />
                  )}
                  {showStreamActivity && !feedbackIsStillGenerating && (
                    <StreamActivityDropdown entries={streamActivityEntries} />
                  )}
                  {/* {generation.content && message.toolsStatus && message.toolsStatus[Tools.SEARCH] && (
            <span className="inline-block italic bg-[#e9e9e980] px-2 py-1 rounded my-1">
              {t('SEARCH_RESULT')}:{' '}
            </span>
          )} */}
                  {message.role === 'user' && isEditing ? (
                    <div className="relative w-full">
                      <Textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="resize-none shadow-none border-none focus-visible:shadow-none focus-visible:border-none focus-visible:ring-0 min-h-[48px] px-3 py-1 pr-20 rounded-md outline-none whitespace-pre-wrap break-words overflow-hidden"
                        style={{
                          fontFamily: 'inherit',
                          fontSize: '0.875rem',
                          lineHeight: '1.25rem',
                          color: theme === 'dark' ? '#e5e5e5' : '#1a1a1a',
                          minHeight: '80px',
                          maxHeight: '160px',
                          backgroundColor: 'transparent',
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            handleSaveEdit(`${message.recordId}`);
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCancelEdit();
                          }
                        }}
                      />
                      <div className="absolute bottom-2 right-2 flex gap-2 items-center">
                        <Button
                          variant="outline"
                          aria-label="Cancel"
                          className="disabled:opacity-40 flex items-center justify-center h-8 hover:bg-muted rounded-full border w-8"
                          onClick={handleCancelEdit}
                        >
                          <Xmark width={16} height={16} strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="outline"
                          disabled={!editedContent.trim()}
                          aria-disabled="false"
                          aria-label="Send message"
                          className="disabled:opacity-40 flex items-center justify-center h-8 hover:bg-muted rounded-full border w-8"
                          onClick={() => handleSaveEdit(`${message.recordId}`)}
                        >
                          <ArrowUp width={16} height={16} strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  ) : message.role === 'assistant' ? (
                    <div id={`chat-message-content-${generation.id}`}>
                      <InterleavedStreamContent
                        content={generation.content ?? ''}
                        entries={generation.streamStatusEntries}
                        surfaces={generation.a2uiSurfaces}
                        streamBlocks={generation.streamBlocks}
                        isStreaming={feedbackIsStillGenerating}
                        renderText={(text) => (
                          <StreamingContent
                            content={text}
                            isUserMessage={false}
                            isAnalyzing={showAnalyzing}
                            citation={citation}
                          />
                        )}
                        renderSurfaces={(surfaces) => (
                          <MessageA2uiSurfaces
                            surfaces={surfaces}
                            userMessageId={message.userRecordId ?? generation.parentUserGenerationId}
                            assistantMessageId={generation.id}
                            isLatest={isLatestTurn}
                          />
                        )}
                      />
                      {generation.isCancelled && <CancelledResponseNotice />}
                      <CreditsEmptyNotice category={generation.errorCategory} code={generation.errorCode} />
                      <TryAgentBadgesList changes={generation.platformResourceChanges} />
                    </div>
                  ) : (
                    <StreamingContent
                      content={generation.content}
                      isUserMessage={message.role === 'user'}
                      isAnalyzing={showAnalyzing}
                    />
                  )}
                </div>
              </div>
              )}
            </div>

            <>
              {message.role === 'assistant' &&
                assistantAttachments &&
                assistantAttachments.length > 0 && (
                  <div className="flex w-full min-w-0 flex-wrap justify-start gap-2 mb-2">
                    {assistantAttachments.map((file) => (
                      <AssistantAttachmentPreview key={file.fileId} file={file} />
                    ))}
                  </div>
                )}
            </>
            <>
              {message.role === 'assistant' &&
                assistantGeneratedImages &&
                assistantGeneratedImages.length > 0 && (
                  <div className="flex w-full min-w-0 flex-wrap justify-start gap-2 mb-2">
                    {assistantGeneratedImages.map((file) => (
                      <AssistantAttachmentPreview key={file.fileId} file={file} />
                    ))}
                  </div>
                )}
            </>
            <>
              {message.role === 'assistant' && sources && sources.length > 0 && (
                <div className="flex items-start justify-start gap-2 mb-2 ml-4">
                  <p className="text-[10px] font-semibold">{t('SOURCES')}:</p>
                  <div className="flex flex-wrap justify-start gap-2 mb-2">
                    {sources.map((source) => (
                      <SourceFile
                        key={source.fileId}
                        sourceFile={source}
                        domId={pillDomId(generation.id, source)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
            <div>
              {message.role == 'assistant' && (
                <>
                  <div className="flex items-center gap-1 justify-start md:pl-3 lg:pl-3">
                    {!isOnboardingChat &&
                      !feedbackIsStillGenerating &&
                      message.generations.length > 1 && (
                      <ChangeMessage
                        recordId={message.recordId}
                        currentMessageIndex={message.activeGenerationIndex ?? 0}
                        totalMessageCount={message.generations.length}
                        isGenerating={feedbackIsStillGenerating}
                      />
                    )}
                    {!feedbackIsStillGenerating && (
                      <CopyMessageDropdown
                        content={generation.content}
                        generationId={generation.id}
                        generatedImages={generation.generatedImages}
                      />
                    )}

                    {!isOnboardingChat && (
                      <Feedback
                        recordId={
                          resolveUserMessageRecordIdForAssistant(displayMessages, message) ??
                          message.userRecordId ??
                          ''
                        }
                        message={generation}
                        isStillGenerating={feedbackIsStillGenerating}
                        hasParticipants={false}
                        conversationResponseGenerating={isStillGenerating}
                        agentModelLabel={agentModelLabel}
                        hideRegenerateButton={
                          generationHasPendingApproval(generation) ||
                          conversationTurnHasPendingApproval(displayMessages, message.userRecordId)
                        }
                      />
                    )}
                  </div>
                </>
              )}
              {message.role == 'user' && !isEditing && (
                <div className="flex items-center gap-1 justify-end">
                  {messageTimeLabel ? (
                    <span
                      className={cn(
                        'hidden h-8 items-center text-xs group-hover:inline-flex',
                        theme === 'dark' ? 'text-[#ffffff]' : 'text-[#888888]'
                      )}
                    >
                      {messageTimeLabel}
                    </span>
                  ) : null}
                  {!isOnboardingChat &&
                    !feedbackIsStillGenerating &&
                    message.generations.length > 1 && (
                    <ChangeMessage
                      recordId={message.recordId}
                      currentMessageIndex={message.activeGenerationIndex ?? 0}
                      totalMessageCount={message.generations.length}
                      isGenerating={feedbackIsStillGenerating}
                    />
                  )}
                  {copyChatBubbleIcons.map((icon) => (
                    <button
                      title={icon.tooltip}
                      key={icon.type}
                      className="flex items-center justify-center h-8 w-8 p-1.5 rounded-full hover:bg-muted"
                      onClick={() => icon.onClick(generation.content)}
                    >
                      {icon.icon}
                    </button>
                  ))}
                  {!isOnboardingChat &&
                    (!userAttachments || userAttachments.length === 0) && (
                    <button
                      type="button"
                      className="flex items-center justify-center h-8 w-8 p-1.5 rounded-full hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
                      disabled={
                        isStillGenerating && message.recordId !== streamingUserMessageRecordId
                      }
                      onClick={() =>
                        handleEditMessage(
                          `${message.recordId}-${generation.id}`,
                          generation.content,
                          message.recordId
                        )
                      }
                      title={t('EDIT') || 'Edit'}
                    >
                      <EditPencil {...CHAT_MESSAGE_ACTION_ICON_PROPS} />
                    </button>
                  )}
                </div>
              )}
            </div>
            {message.role === 'assistant' && (
              <SearchRelatedImageList
                isSkeletonShowForImages={generation.isSkeletonShowForImages}
                messageId={message.recordId}
                imageList={generation.relatedSearcheImageList}
              />
            )}
            {message.role === 'assistant' && (
              <SearchRelatedVideoList
                isSkeletonShowForVideos={generation.isSkeletonShowForVideos}
                messageId={message.recordId}
                videoList={generation.relatedSearcheVideoList}
              />
            )}
            {message.role === 'assistant' && (
              <SearchRelatedList
                isSkeletonShowForQuestions={generation.isSkeletonShowForQuestions}
                messageId={message.recordId}
                onSendMessage={handleSendMessage}
                questionList={generation.relatedSearcheQuestionsList}
                sendDisabled={isStillGenerating}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="mb-[130px] flex flex-col gap-1.5">
        {associatedAssistant && <CustomGptHeader assistant={associatedAssistant} />}
        {displayMessages.map((message, index) => {
          const stableRecordId = message.recordId || message.generations?.[0]?.id || String(index);
          const messageKey = `${message.role}-${stableRecordId}`;

          const userActiveGenIndex = message.activeGenerationIndex ?? 0;
          const userActiveGenId = message.generations[userActiveGenIndex]?.id;
          const isThisStreamingAssistant =
            streamingMessage != null &&
            (message.recordId === streamingMessage.recordId ||
              (streamingAssistantMessageId != null &&
                message.generations.some((g) => g.id === streamingAssistantMessageId)));
          const streamingGenerationIndex = getStreamingGenerationIndex(
            message,
            streamingAssistantMessageId
          );
          const generationForStreamUi = mergeStreamStatusEntriesFromGenerations(
            message.generations,
            streamingGenerationIndex
          );
          const streamingUserMessageRecordId =
            streamingMessage != null
              ? resolveUserMessageRecordIdForAssistant(displayMessages, streamingMessage)
              : undefined;
          const messageUserMessageRecordId =
            message.role === 'assistant'
              ? resolveUserMessageRecordIdForAssistant(displayMessages, message)
              : undefined;
          const isStaleAssistantForActiveStream =
            isStillGenerating &&
            streamingMessage != null &&
            message.role === 'assistant' &&
            !isThisStreamingAssistant &&
            streamingUserMessageRecordId != null &&
            messageUserMessageRecordId === streamingUserMessageRecordId;
          if (message.role === 'user') {
            return (
              <div
                key={messageKey}
                data-chat-request={message.recordId === latestRequestId ? 'latest' : undefined}
                className={`group flex ${editingMessageId === `${message.recordId}-${userActiveGenId}` ? 'justify-start' : 'justify-end'} items-start gap-0.5 mb-2`}
              >
                <div
                  className={
                    editingMessageId === `${message.recordId}-${userActiveGenId}`
                      ? 'w-full'
                      : 'max-w-[90%] relative'
                  }
                >
                  {messageBubbleUi(message, userActiveGenIndex, isStillGenerating)}
                </div>
              </div>
            );
          }

          if (isStaleAssistantForActiveStream) {
            return null;
          }

          if (message.role === MessageRole.APPROVAL) {
            const approvalGeneration = message.generations[message.activeGenerationIndex ?? 0];
            const approvalSurfaces = approvalGeneration?.a2uiSurfaces;
            if (!approvalSurfaces?.length) return null;
            return (
              <div key={messageKey} className="flex w-full min-w-0 items-start gap-1">
                <div className="relative min-w-0 flex-1 pl-9">
                  <MessageA2uiSurfaces
                    surfaces={approvalSurfaces}
                    userMessageId={message.userRecordId ?? approvalGeneration.parentUserGenerationId}
                    assistantMessageId={
                      approvalGeneration.parentIds?.[0] ?? latestAssistantRecordId
                    }
                    isLatest={isLatestApprovalTurn(displayMessages, index)}
                  />
                </div>
              </div>
            );
          }

          if (message.role === 'assistant') {
            const streamGeneration = generationForStreamUi;

            if (shouldRenderAssistantStreamingRow(message, streamGeneration, isStillGenerating)) {
              return (
                <AssistantStreamingRow
                  key={messageKey}
                  generation={streamGeneration}
                  agentId={agentId}
                  agentLogoUrl={agentLogoUrl}
                />
              );
            }

            return (
              <div key={messageKey} className="group flex w-full min-w-0 items-start gap-1">
                <AssistantAvatar
                  agentId={agentId}
                  agentLogoUrl={agentLogoUrl}
                  className="mt-1"
                />

                <div className="relative min-w-0 flex-1">
                  {messageBubbleUi(
                    message,
                    isThisStreamingAssistant
                      ? streamingGenerationIndex
                      : resolveDisplayGenerationIndex(message),
                    isStillGenerating,
                    isLatestApprovalTurn(displayMessages, index)
                  )}
                </div>
              </div>
            );
          }

          return null;
        })}

        {isStillGenerating &&
          isLoading &&
          !displayMessages.some((message) => {
            if (message.role !== 'assistant') return false;
            const streamGeneration = mergeStreamStatusEntriesFromGenerations(
              message.generations,
              getStreamingGenerationIndex(message, streamingAssistantMessageId)
            );
            return shouldRenderAssistantStreamingRow(
              message,
              streamGeneration,
              isStillGenerating
            );
          }) && (
            <AssistantStreamingRow
              key="assistant-stream-pending"
              agentId={agentId}
              agentLogoUrl={agentLogoUrl}
            />
          )}

        <div ref={messagesEndRef} />
      </div>
      {scrollButtonPortalHost
        ? createPortal(scrollToBottomButtonUi, scrollButtonPortalHost)
        : scrollToBottomButtonUi}
    </>
  );
};
